/**
 * BookingDetailModal Component
 * Modal displaying full booking details, with itemized view for Room Service orders
 */

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Booking, CalendarEvent } from '@/types';
import { ServiceCategory, BookingStatus, CalendarEventStatus } from '@/types';

interface BookingDetailModalProps {
  isOpen: boolean;
  booking?: Booking | null;
  event?: CalendarEvent | null;
  onClose: () => void;
}

function getCategoryColor(category: ServiceCategory): string {
  const colors: Record<ServiceCategory, string> = {
    [ServiceCategory.SPA]: 'from-purple-600 to-purple-800',
    [ServiceCategory.RESTAURANT]: 'from-rose-600 to-rose-800',
    [ServiceCategory.BAR]: 'from-fuchsia-600 to-fuchsia-800',
    [ServiceCategory.ROOM_SERVICE]: 'from-sky-600 to-sky-800',
    [ServiceCategory.HOUSEKEEPING]: 'from-emerald-600 to-emerald-800',
    [ServiceCategory.HOTEL_INFO]: 'from-cyan-600 to-cyan-800',
    [ServiceCategory.ACTIVITY]: 'from-amber-600 to-amber-800',
    [ServiceCategory.TRANSPORT]: 'from-indigo-600 to-indigo-800',
  };
  return colors[category] || 'from-cyan-600 to-cyan-800';
}

function getCategoryIcon(category: ServiceCategory): string {
  const icons: Record<ServiceCategory, string> = {
    [ServiceCategory.SPA]: '💆',
    [ServiceCategory.RESTAURANT]: '🍽️',
    [ServiceCategory.BAR]: '🍸',
    [ServiceCategory.ROOM_SERVICE]: '🛎️',
    [ServiceCategory.HOUSEKEEPING]: '🧹',
    [ServiceCategory.HOTEL_INFO]: '📅',
    [ServiceCategory.ACTIVITY]: '🏛️',
    [ServiceCategory.TRANSPORT]: '🚕',
  };
  return icons[category] || '📅';
}

function getStatusBadge(status: BookingStatus | CalendarEventStatus | string): { text: string; color: string } {
  if (status === BookingStatus.CONFIRMED || status === CalendarEventStatus.CONFIRMED) return { text: 'Confirmed', color: 'bg-green-500' };
  if (status === BookingStatus.PENDING)     return { text: 'Pending',    color: 'bg-yellow-500' };
  if (status === BookingStatus.COMPLETED)   return { text: 'Completed',  color: 'bg-blue-500' };
  if (status === BookingStatus.CANCELLED || status === CalendarEventStatus.CANCELLED) return { text: 'Cancelled', color: 'bg-red-500' };
  if (status === BookingStatus.ON_HOLD)     return { text: 'On Hold',    color: 'bg-orange-500' };
  if (status === BookingStatus.IN_PROGRESS) return { text: 'In Progress', color: 'bg-sky-500' };
  return { text: String(status), color: 'bg-slate-500' };
}

export function BookingDetailModal({ isOpen, booking, event, onClose }: BookingDetailModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    // Use capture phase to handle ESC before CalendarView
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen || (!booking && !event)) return null;

  const isBooking = !!booking;
  const category = isBooking ? booking!.category : event!.serviceType;
  const title = isBooking ? booking!.serviceName : event!.title;
  const datetime = new Date(isBooking ? booking!.datetime : event!.datetime);
  const duration = isBooking ? booking!.durationMinutes : event!.durationMinutes;
  const location = isBooking ? booking!.location : event!.location;
  const status = isBooking ? booking!.status : event!.status;
  const statusBadge = getStatusBadge(status);

  const formatDate = (date: Date) => date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (date: Date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const formatEndTime = (startDate: Date, mins: number) => { const end = new Date(startDate); end.setMinutes(end.getMinutes() + mins); return formatTime(end); };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          className="bg-zinc-900 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
          <div className={`p-6 bg-gradient-to-r ${getCategoryColor(category)}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <span className="text-5xl">{getCategoryIcon(category)}</span>
                <div>
                  <h2 className="text-2xl font-playfair text-white">{title}</h2>
                  <span className={`px-2 py-1 mt-2 inline-block rounded-full text-xs font-medium text-white ${statusBadge.color}`}>{statusBadge.text}</span>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30">✕</button>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">📅</div>
              <div>
                <p className="text-white font-medium">{formatDate(datetime)}</p>
                <p className="text-white/60">{formatTime(datetime)} - {formatEndTime(datetime, duration)} <span className="text-white/40 ml-2">({duration}min)</span></p>
              </div>
            </div>
            {location && (
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">📍</div>
                <div><p className="text-white/60 text-sm">Location</p><p className="text-white font-medium">{location}</p></div>
              </div>
            )}
            {isBooking && booking!.price !== null && (
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">💰</div>
                <div><p className="text-white/60 text-sm">Total</p><p className="text-2xl font-semibold text-teal-400">€{booking!.price}</p></div>
              </div>
            )}
            {isBooking && booking!.items && booking!.items.length > 0 && (
              <div className="p-4 bg-white/5 rounded-xl">
                <h3 className="text-white/60 text-sm mb-3">Order Items</h3>
                <div className="space-y-3">
                  {booking!.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white">{item.quantity}</span>
                        <span className="text-white">{item.name}</span>
                      </div>
                      <span className="text-white/60">€{(item.quantity * item.unitPrice).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-3 border-t border-white/10 flex items-center justify-between">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-teal-400 font-semibold">€{booking!.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-4 bg-black/30 border-t border-white/10">
            <div className="flex items-center justify-center text-white/40 text-sm"><span>Press Esc to close</span></div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default BookingDetailModal;
